/**
 * What the assistant just did, and what went wrong.
 *
 * Everything in this folder fails quietly on purpose. A hosted voice that
 * refuses falls through to the browser's rather than leaving somebody with
 * silence; a model that cannot be reached says one gentle sentence rather than
 * an HTTP status; a question nothing matches becomes "I'm not sure" rather than
 * a stack trace. That is right for the grandmother this is built for and it is
 * indefensible for whoever has to make it work, who was left with a screen that
 * behaved oddly and no way at all to find out why. Jay: *"It's becoming very
 * hard to debug all these issues, 'cause they just randomly happen… is there any
 * way to easily debug when something's wrong with an agent? Like an error log?"*
 *
 * So: every question asked, every answer, every voice call, every failure, with
 * the exact words the service sent back, kept in a ring in memory and shown on
 * one screen in **Admin controls → Assistant**. Not the browser console — the
 * failures that matter most happen on a phone, where there is no console, held
 * by somebody who is not going to open one.
 *
 * Three deliberate constraints:
 *
 *  - **In memory, never stored.** Questions people ask the assistant are as
 *    personal as anything else in this app, and a transcript that survives a
 *    reload is a transcript somebody has to think about deleting. This one
 *    exists for as long as the page is open and not a moment longer.
 *  - **A fixed ring.** Fifty entries. An unbounded log in a page left open for a
 *    week is a memory leak with good intentions.
 *  - **It never throws.** A logger that can break the thing it is watching is
 *    worse than no logger, so every call here is wrapped and silent on failure.
 */

export type LogLevel = 'ok' | 'warn' | 'fail';

export interface LogEntry {
  id: number;
  at: number;
  level: LogLevel;
  /** Which part spoke: `model`, `voice`, `ear`, `tool`, `setup`. */
  area: 'model' | 'voice' | 'ear' | 'tool' | 'setup';
  /** One short line, in English, for whoever is debugging. Never shown to a family member. */
  what: string;
  /** The exact words the service sent back, when there were any. */
  detail?: string;
}

const MAX = 50;

let seq = 0;
let entries: LogEntry[] = [];
const listeners = new Set<(e: LogEntry[]) => void>();

/**
 * Write one line.
 *
 * `detail` is deliberately not translated and deliberately not cleaned up: the
 * whole value of it is that it is what the service actually said, which is the
 * thing you paste into a search or hand to somebody else.
 */
export function logAssistant(area: LogEntry['area'], level: LogLevel, what: string, detail?: unknown): void {
  try {
    const entry: LogEntry = {
      id: ++seq,
      at: Date.now(),
      level,
      area,
      what,
      detail: detail === undefined ? undefined : describe(detail),
    };
    entries = [entry, ...entries].slice(0, MAX);
    for (const l of listeners) l(entries);
    // Still the console too, for whoever does have one open.
    if (level !== 'ok') console.warn(`[assistant] ${area}: ${what}`, detail ?? '');
  } catch {
    /* a logger may never be the reason something broke */
  }
}

/** Whatever was handed in, as one line somebody can read. */
function describe(detail: unknown): string {
  if (typeof detail === 'string') return detail.slice(0, 500);
  if (detail instanceof Error) return `${detail.name}: ${detail.message}`.slice(0, 500);
  try {
    return JSON.stringify(detail).slice(0, 500);
  } catch {
    return String(detail).slice(0, 500);
  }
}

export function assistantLog(): LogEntry[] {
  return entries;
}

export function onAssistantLog(listener: (e: LogEntry[]) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function clearAssistantLog(): void {
  entries = [];
  for (const l of listeners) l(entries);
}

/**
 * The whole log as plain text, for pasting somewhere.
 *
 * The point of a debugging screen on a phone is that what you find there can
 * leave the phone. Timestamps are relative, because "14s ago" is what you
 * actually want to know and a wall-clock time on someone else's device is not.
 */
export function assistantLogAsText(): string {
  const now = Date.now();
  return entries
    .slice()
    .reverse()
    .map((e) => {
      const ago = Math.round((now - e.at) / 1000);
      return `${String(ago).padStart(4)}s  ${e.level.toUpperCase().padEnd(4)} ${e.area.padEnd(5)} ${e.what}${e.detail ? `\n            ${e.detail}` : ''}`;
    })
    .join('\n');
}
